import { forwardRef, type ButtonHTMLAttributes, type ReactNode, type AnchorHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

type Common = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  /** Icon-only button; `aria-label` becomes required in practice. */
  iconOnly?: boolean;
  icon?: ReactNode;
  iconEnd?: ReactNode;
};

export function buttonClass({ variant = 'secondary', size = 'md', block, iconOnly }: Common, extra?: string): string {
  return [
    'ui-btn',
    `ui-btn--${variant}`,
    size !== 'md' && `ui-btn--${size}`,
    block && 'ui-btn--block',
    iconOnly && 'ui-btn--icon',
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

type ButtonProps = Common & ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, block, iconOnly, icon, iconEnd, loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClass({ variant, size, block, iconOnly }, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="ui-spinner ui-btn__spinner" aria-hidden /> : icon}
      {children}
      {iconEnd}
    </button>
  );
});

type ButtonLinkProps = Common & LinkProps;

export function ButtonLink({ variant, size, block, iconOnly, icon, iconEnd, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClass({ variant, size, block, iconOnly }, className)} {...rest}>
      {icon}
      {children}
      {iconEnd}
    </Link>
  );
}

type ButtonAnchorProps = Common & AnchorHTMLAttributes<HTMLAnchorElement>;

export function ButtonAnchor({ variant, size, block, iconOnly, icon, iconEnd, className, children, ...rest }: ButtonAnchorProps) {
  return (
    <a className={buttonClass({ variant, size, block, iconOnly }, className)} {...rest}>
      {icon}
      {children}
      {iconEnd}
    </a>
  );
}
